// src/hooks/api/useAuth.js
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { serverRequest, serverRequestNoBearer } from '../../util/server_request';
import { notifyUser, logout } from '../../util/util';
import secureLocalStorage from 'react-secure-storage';

// Query Keys
export const authKeys = {
  all: ['auth'],
  login: () => [...authKeys.all, 'login'],
  user: () => [...authKeys.all, 'user'],
  verification: (type) => [...authKeys.all, 'verification', type],
};

// Login mutation
export const useLogin = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (loginData) => {
      // Use serverRequestNoBearer for login since user isn't authenticated yet
      return await serverRequestNoBearer('POST', 'auth/login', loginData);
    },
    onSuccess: (data) => {
      
      if (data.success && (data.data?.accessToken || data.data?.authToken)) {
        // Handle both possible token field names
        const token = data.data.accessToken || data.data.authToken;
        
        
        secureLocalStorage.setItem('token', token);
        secureLocalStorage.setItem('refreshToken', data.data.refreshToken);
        secureLocalStorage.setItem('expiresAt', data.data.expiresAt);
        
        // Ensure user data is properly stored as string
        const userData = typeof data.data.user === 'string' 
          ? data.data.user 
          : JSON.stringify(data.data.user);
        secureLocalStorage.setItem('user', userData);
        
        
        notifyUser('success', 'Login successful!', 'Welcome');
        queryClient.invalidateQueries({ queryKey: authKeys.all });
      } else {
      }
    },
    onError: (error) => {
      notifyUser('error', error.message || 'Login failed', 'Error');
    },
  });
};

// Forgot password mutation
export const useForgotPassword = () => {
  return useMutation({
    mutationFn: async (phoneData) => {
      return await serverRequestNoBearer('POST', 'auth/password/forgot', phoneData);
    },
    onSuccess: (data) => {
      if (data.success) {
        notifyUser('success', data.message, 'Success');
      }
    },
    onError: (error) => {
      notifyUser('error', error.message || 'Request failed', 'Error');
    },
  });
};

// Verify employer number mutation
export const useVerifyEmployerNumber = () => {
  return useMutation({
    mutationFn: async (employerData) => {
      return await serverRequestNoBearer('POST', 'auth/corporate/verify/employer-number', employerData);
    },
    onSuccess: (data) => {
      if (data?.code === '200') {
        notifyUser('success', data.message, 'Success');
      }
    },
    onError: (error) => {
      notifyUser('error', error.message || 'Verification failed', 'Error');
    },
  });
};

// Send OTP mutation
export const useSendOTP = () => {
  return useMutation({
    mutationFn: async (phoneData) => {
      return await serverRequestNoBearer('POST', 'auth/phone-verification/otp/send', phoneData);
    },
    onSuccess: (data) => {
      if (data?.code === '400' || data.success) {
        notifyUser('success', data.message || `OTP sent to ${data.phone}`, 'OTP');
      }
    },
    onError: (error) => {
      notifyUser('error', error.message || 'OTP send failed', 'Validation Error');
    },
  });
};

// Verify OTP mutation
export const useVerifyOTP = () => {
  return useMutation({
    mutationFn: async ({ otp, phone }) => {
      return await serverRequestNoBearer('GET', `auth/verify/phone/otp?otp=${otp}&phone=${phone}`);
    },
    onSuccess: (data) => {
      if (data.success) {
        notifyUser('success', data.message, 'Success');
      }
    },
    onError: (error) => {
      notifyUser('error', error.message || 'OTP verification failed', 'Error');
    },
  });
};

// Verify employer details mutation
export const useVerifyEmployerDetails = () => {
  return useMutation({
    mutationFn: async (employerDetails) => {
      return await serverRequestNoBearer('POST', 'auth/verify/employer/details', employerDetails);
    },
    onSuccess: (data) => {
      if (data.success) {
        notifyUser('success', data.message, 'Success');
      }
    },
    onError: (error) => {
      notifyUser('error', error.message || 'Verification failed', 'Error');
    },
  });
};

// Create password mutation
export const useCreatePassword = () => {
  return useMutation({
    mutationFn: async (passwordData) => {
      return await serverRequestNoBearer('POST', 'auth/create/password', passwordData);
    },
    onSuccess: (data) => {
      if (data.success) {
        notifyUser('success', data.message, 'Success');
      }
    },
    onError: (error) => {
      notifyUser('error', error.message || 'Password creation failed', 'Error');
    },
  });
};

// Verify email code mutation
export const useVerifyEmailCode = () => {
  return useMutation({
    mutationFn: async ({ code, email }) => {
      return await serverRequestNoBearer('GET', `auth/verify/email/code?code=${code}&email=${email}`);
    },
    onSuccess: (data) => {
      if (data.success) {
        notifyUser('success', 'Email verification successful!', 'Verified');
      }
    },
    onError: (error) => {
      notifyUser('error', error.message || 'Email verification failed', 'Error');
    },
  });
};

// Send email verification code mutation
export const useSendEmailVerification = () => {
  return useMutation({
    mutationFn: async (emailData) => {
      return await serverRequest('POST', 'auth/email-verification/code/send', emailData);
    },
    onSuccess: (data) => {
      if (data.success) {
        notifyUser('success', 'Email triggered successfully!', 'Email Verification');
      }
    },
    onError: (error) => {
      notifyUser('error', error.message || 'Email send failed', 'Error');
    },
  });
};

// Verify account PIN mutation
export const useVerifyAccountPin = () => {
  return useMutation({
    mutationFn: async ({ phone, pin, otp }) => {
      return await serverRequest('POST', 'auth/verify/account/pin', { phone, pin }, true, otp);
    },
    onSuccess: (data) => {
      if (data.success) {
        notifyUser('success', data.message, 'Success');
      }
    },
    onError: (error) => {
      notifyUser('error', error.message || 'PIN verification failed', 'Error');
    },
  });
};

// Logout helper
export const useLogout = () => {
  const queryClient = useQueryClient();
  
  return () => {
    logout();
    queryClient.clear();
  };
};
